/**
 * The user's role on a team, as a pure function of the teams array.
 *
 * It lived inside `TaskBoardTab` as a `useCallback` over a memoised map, which
 * was fine while the board UI was the only thing that needed it. MCP board
 * writes need the same predicate and cannot reach a component, and the failure
 * it prevents is not cosmetic: a team-board push from a viewer is rejected by
 * the server (`team_sync_controller.ex`), and a rejected push **poisons that
 * scope's outbox forever with no event reaching JS** — unrecoverable from the
 * client. So the answer has to be the same one the UI already uses, not a
 * second implementation that can drift into disagreeing with it.
 *
 * Nothing here touches a store, the DOM or the clock — the caller supplies the
 * teams array, which is what lets the board tab pass its subscribed value and
 * `boardsStore` pass `useTeamsStore.getState()`.
 *
 * `null` means "no role known", which is NOT the same as viewer: a team that is
 * absent from the array (the background teams fetch has not landed, or the user
 * is not a member) resolves to null here, and the caller's own scope-resolution
 * step is what refuses that case. Mirroring the UI exactly is the point — a
 * stricter rule here would refuse writes the app itself allows.
 */
export function roleForTeam(teams, teamId) {
  if (teamId == null) return null;
  const wanted = String(teamId);
  const team = (teams || []).find((t) => t?.id != null && String(t.id) === wanted);
  if (!team) return null;
  return team.role || (team.is_owner ? 'owner' : null);
}

/** True only for an explicit `viewer` role — see the null note above. */
export function isViewerRole(teams, teamId) {
  return roleForTeam(teams, teamId) === 'viewer';
}
